import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, Volume2, VolumeX, ChevronRight } from 'lucide-react';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import AudioTip from '../components/brand/AudioTip';
import { useMeditationContent } from '../hooks/useMeditationContent';
import { CoRegulateMark } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Body Scan Meditation
 * A guided practice to develop body awareness and release tension
 * Audio-guided with visual progress indicator
 */

// Default steps (fallback if API fails)
const DEFAULT_BODY_SCAN_STEPS = [
    {
        id: 'intro',
        bodyPart: 'Introduction',
        duration: 15,
        instruction: "Find a comfortable position. . . You can sit or lie down. . . Let your eyes close gently. . . Take a few deep breaths. . . to settle in.",
        guidance: "There's nothing to fix or change. . . Just notice what's there."
    },
    {
        id: 'feet',
        bodyPart: 'Feet',
        duration: 20,
        instruction: "Bring your attention to your feet. . . Notice any sensations. . . warmth. . . coolness. . . tingling. . . pressure against the floor.",
        guidance: "Just observe. . . No need to change anything."
    },
    {
        id: 'lower-legs',
        bodyPart: 'Lower Legs',
        duration: 20,
        instruction: "Move your attention up to your lower legs. . . your ankles. . . calves. . . shins. . . Notice what's there.",
        guidance: "If you notice tension. . . that's okay. . . Just acknowledge it."
    },
    {
        id: 'upper-legs',
        bodyPart: 'Upper Legs',
        duration: 20,
        instruction: "Now your thighs and knees. . . Feel the weight of your legs. . . Notice any areas of holding. . . or ease.",
        guidance: "Your body is always communicating. . . This is how we listen."
    },
    {
        id: 'pelvis',
        bodyPart: 'Pelvis & Hips',
        duration: 20,
        instruction: "Bring awareness to your pelvis. . . hips. . . and lower belly. . . This area often holds emotion.",
        guidance: "Breathe gently into this space. . . Let it soften if it wants to."
    },
    {
        id: 'belly',
        bodyPart: 'Belly',
        duration: 20,
        instruction: "Notice your belly. . . rising and falling with each breath. . . Feel the movement. . . the rhythm.",
        guidance: "The belly is our emotional center. . . What do you notice here?"
    },
    {
        id: 'chest',
        bodyPart: 'Chest',
        duration: 20,
        instruction: "Move to your chest and heart space. . . Notice your heartbeat if you can. . . Feel the breath moving here.",
        guidance: "This is where we hold both grief and joy. . . Whatever is here. . . is welcome."
    },
    {
        id: 'back',
        bodyPart: 'Back',
        duration: 20,
        instruction: "Scan your entire back. . . lower. . . middle. . . upper. . . Notice areas of tension. . . or ease.",
        guidance: "The back often carries what we don't want to face. . . Just notice."
    },
    {
        id: 'shoulders',
        bodyPart: 'Shoulders & Arms',
        duration: 20,
        instruction: "Bring attention to your shoulders. . . arms. . . hands. . . and fingers. . . Notice any gripping. . . or relaxation.",
        guidance: "Let your shoulders drop if they're raised. . . Let your hands soften."
    },
    {
        id: 'neck-throat',
        bodyPart: 'Neck & Throat',
        duration: 20,
        instruction: "Notice your neck and throat. . . This area holds what we haven't said. . . what we've swallowed.",
        guidance: "Gently swallow. . . and notice the sensation."
    },
    {
        id: 'face',
        bodyPart: 'Face',
        duration: 20,
        instruction: "Scan your face. . . jaw. . . cheeks. . . eyes. . . forehead. . . Notice where you hold expression. . . where you mask.",
        guidance: "Let your jaw unclench. . . Let your brow smooth."
    },
    {
        id: 'head',
        bodyPart: 'Head',
        duration: 15,
        instruction: "Notice your whole head. . . the scalp. . . the space behind your eyes. . . the weight of thoughts.",
        guidance: "Your mind lives here. . . Notice it. . . without getting caught in it."
    },
    {
        id: 'whole-body',
        bodyPart: 'Whole Body',
        duration: 20,
        instruction: "Now feel your entire body as one. . . From the top of your head. . . to the tips of your toes. . . One whole. . . breathing being.",
        guidance: "You are here. . . You are whole. . . You are enough."
    },
    {
        id: 'closing',
        bodyPart: 'Closing',
        duration: 15,
        instruction: "Begin to deepen your breath. . . Wiggle your fingers and toes. . . When you're ready. . . gently open your eyes.",
        guidance: "Carry this awareness with you. . . Your body is always here for you."
    }
];

const BodyScan = () => {
    const navigate = useNavigate();
    
    // Fetch content from database (falls back to defaults)
    const { steps: BODY_SCAN_STEPS, loading: contentLoading } = useMeditationContent('body-scan-main', DEFAULT_BODY_SCAN_STEPS);
    
    const [currentStep, setCurrentStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(DEFAULT_BODY_SCAN_STEPS[0].duration);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [musicEnabled, setMusicEnabled] = useState(true);
    const [musicVolume] = useState(0.35); // Keep music soft behind voice
    const audioRef = useRef(null);
    const musicRef = useRef(null);
    const timerRef = useRef(null);

    // Update timeRemaining when steps load from API
    useEffect(() => {
        if (BODY_SCAN_STEPS.length > 0 && !isPlaying) {
            setTimeRemaining(BODY_SCAN_STEPS[0].duration);
        }
    }, [BODY_SCAN_STEPS, isPlaying]);

    const step = BODY_SCAN_STEPS[currentStep] || DEFAULT_BODY_SCAN_STEPS[0];
    const progress = ((currentStep + 1) / BODY_SCAN_STEPS.length) * 100;
    const totalDuration = BODY_SCAN_STEPS.reduce((sum, s) => sum + s.duration, 0);

    // Background music control
    useEffect(() => {
        if (musicRef.current) {
            musicRef.current.volume = musicVolume;
            musicRef.current.loop = true;
        }
    }, [musicVolume]);

    // Start/stop background music with session
    useEffect(() => {
        if (musicRef.current && musicEnabled) {
            if (isPlaying && !isPaused) {
                musicRef.current.play().catch(() => {});
            } else {
                musicRef.current.pause();
            }
        }
    }, [isPlaying, isPaused, musicEnabled]);

    // Lower music volume during voice guidance
    useEffect(() => {
        if (musicRef.current && musicEnabled) {
            if (isAudioPlaying) {
                musicRef.current.volume = musicVolume * 0.3; // Duck music during voice
            } else {
                musicRef.current.volume = musicVolume;
            }
        }
    }, [isAudioPlaying, musicEnabled, musicVolume]);

    // Generate audio for current step - returns a promise
    const generateAudio = async (stepData) => {
        if (!audioEnabled) return Promise.resolve();
        
        // Stop any currently playing audio first
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        
        setIsAudioPlaying(true);
        
        try {
            const response = await fetch(`${API_URL}/api/tts/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: `${stepData.instruction} . . . ${stepData.guidance}`,
                    voice: 'alloy',
                    speed: 0.85
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (audioRef.current) {
                    return new Promise((resolve) => {
                        audioRef.current.src = `data:audio/mp3;base64,${data.audio}`;
                        audioRef.current.onended = () => {
                            setIsAudioPlaying(false);
                            resolve();
                        };
                        audioRef.current.onerror = () => {
                            setIsAudioPlaying(false);
                            resolve();
                        };
                        audioRef.current.play().catch(() => {
                            setIsAudioPlaying(false);
                            resolve();
                        });
                    });
                }
            }
        } catch (error) {
            console.error('Audio generation failed:', error);
            setIsAudioPlaying(false);
        }
        return Promise.resolve();
    };

    // Timer logic - only counts down when audio is not playing
    useEffect(() => {
        if (isPlaying && !isPaused && !isAudioPlaying) {
            timerRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
        }
        
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isPlaying, isPaused, isAudioPlaying]);

    // Handle step transitions when timer reaches 0
    useEffect(() => {
        if (timeRemaining === 0 && isPlaying && !isPaused) {
            handleNextStep();
        }
    }, [timeRemaining, isPlaying, isPaused]);

    const handleNextStep = async () => {
        if (currentStep < BODY_SCAN_STEPS.length - 1) {
            const nextStep = BODY_SCAN_STEPS[currentStep + 1];
            setCurrentStep(currentStep + 1);
            await generateAudio(nextStep);
            setTimeRemaining(8); // Brief pause after audio to settle before next step
        } else {
            setIsPlaying(false);
            setIsComplete(true);
        }
    };

    const handleStart = async () => {
        setIsPlaying(true);
        setIsPaused(false);
        await generateAudio(step);
        setTimeRemaining(8); // Brief pause after intro audio
    };

    const handlePause = () => {
        setIsPaused(!isPaused);
        if (audioRef.current) {
            if (!isPaused) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
        }
    };

    const handleReset = () => {
        setCurrentStep(0);
        setTimeRemaining(BODY_SCAN_STEPS[0].duration);
        setIsPlaying(false);
        setIsPaused(false);
        setIsComplete(false);
        setIsAudioPlaying(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
        }
        if (musicRef.current) {
            musicRef.current.pause();
            musicRef.current.currentTime = 0;
        }
    };

    const handleSkipToStep = async (index) => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setCurrentStep(index);
        if (isPlaying) {
            await generateAudio(BODY_SCAN_STEPS[index]);
            setTimeRemaining(8);
        } else {
            setTimeRemaining(BODY_SCAN_STEPS[index].duration);
        }
    };

    // Completion screen
    if (isComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#E8F5F5] to-[#FEF6EF]">
                <header className="p-4">
                    <IconButton onClick={() => navigate('/home')} data-testid="close-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                </header>
                
                <main className="max-w-md mx-auto px-6 py-12 text-center">
                    <div className="w-20 h-20 rounded-full bg-[#7D8E95]/30 flex items-center justify-center mx-auto mb-6">
                        <div className="w-12 h-12 rounded-full bg-[#7D8E95] flex items-center justify-center">
                            <span className="text-white text-2xl">✓</span>
                        </div>
                    </div>
                    
                    <Heading level={1} className="text-[#1D6B6B] mb-4">
                        Body Scan Complete
                    </Heading>
                    
                    <BodyText className="text-[#7D8E95] mb-8 leading-relaxed">
                        You've just spent {Math.round(totalDuration / 60)} minutes connecting with your body. 
                        This awareness stays with you. Notice how you feel right now.
                    </BodyText>
                    
                    <div className="space-y-3">
                        <PrimaryButton onClick={() => navigate('/home')} className="w-full">
                            Return to Dashboard
                        </PrimaryButton>
                        <SecondaryButton onClick={handleReset} className="w-full">
                            Do Another Scan
                        </SecondaryButton>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#E8F5F5] to-[#FEF6EF]">
            {/* Hidden audio elements */}
            <audio ref={audioRef} />
            <audio ref={musicRef} src="/audio/ocean-peace.mp3" loop preload="auto" />
            
            {/* Header */}
            <header className="sticky top-0 z-40 glass-header p-4">
                <div className="flex items-center justify-between">
                    <IconButton onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <SmallText className="text-[#7D8E95]">Body Scan</SmallText>
                    <button
                        onClick={() => navigate('/home')}
                        className="hover:opacity-80 transition-opacity"
                        data-testid="header-logo"
                        aria-label="Go to dashboard"
                    >
                        <CoRegulateMark size={28} />
                    </button>
                </div>
                
                {/* Audio control row */}
                <div className="mt-3 flex items-center justify-center gap-2">
                    <button
                        onClick={() => setAudioEnabled(!audioEnabled)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                            audioEnabled 
                                ? 'bg-[#7D8E95]/30 text-[#7D8E95]' 
                                : 'bg-[#7D8E95]/50 text-[#7D8E95]'
                        }`}
                        data-testid="audio-toggle"
                    >
                        {audioEnabled ? (
                            <>
                                <Volume2 className="w-4 h-4" />
                                <SmallText>Voice On</SmallText>
                            </>
                        ) : (
                            <>
                                <VolumeX className="w-4 h-4" />
                                <SmallText>Voice Off</SmallText>
                            </>
                        )}
                    </button>
                    <button
                        onClick={() => {
                            setMusicEnabled(!musicEnabled);
                            if (musicRef.current) {
                                if (!musicEnabled && isPlaying) {
                                    musicRef.current.play().catch(() => {});
                                } else {
                                    musicRef.current.pause();
                                }
                            }
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                            musicEnabled 
                                ? 'bg-[#7D8E95]/30 text-[#7D8E95]' 
                                : 'bg-[#7D8E95]/50 text-[#7D8E95]'
                        }`}
                        data-testid="music-toggle"
                    >
                        {musicEnabled ? (
                            <SmallText>Music On</SmallText>
                        ) : (
                            <SmallText>Music Off</SmallText>
                        )}
                    </button>
                </div>
                
                {/* Progress bar */}
                <div className="mt-3 h-1.5 bg-[#7D8E95] rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-[#7D8E95] rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 py-8">
                {/* Body visualization */}
                <div className="relative h-64 mb-8 flex items-center justify-center">
                    <div className="relative">
                        {/* Simple body outline with highlighted area */}
                        <svg viewBox="0 0 100 200" className="w-32 h-64">
                            {/* Body outline */}
                            <ellipse cx="50" cy="20" rx="15" ry="18" fill="none" stroke="#7D8E95" strokeWidth="2" opacity={step.bodyPart === 'Head' || step.bodyPart === 'Face' ? 1 : 0.3} />
                            <line x1="50" y1="38" x2="50" y2="45" stroke="#7D8E95" strokeWidth="2" opacity={step.bodyPart === 'Neck & Throat' ? 1 : 0.3} />
                            <ellipse cx="50" cy="70" rx="25" ry="30" fill="none" stroke="#7D8E95" strokeWidth="2" opacity={['Chest', 'Belly', 'Back'].includes(step.bodyPart) ? 1 : 0.3} />
                            <line x1="25" y1="55" x2="10" y2="100" stroke="#7D8E95" strokeWidth="2" opacity={step.bodyPart === 'Shoulders & Arms' ? 1 : 0.3} />
                            <line x1="75" y1="55" x2="90" y2="100" stroke="#7D8E95" strokeWidth="2" opacity={step.bodyPart === 'Shoulders & Arms' ? 1 : 0.3} />
                            <line x1="40" y1="100" x2="35" y2="180" stroke="#7D8E95" strokeWidth="2" opacity={['Upper Legs', 'Lower Legs', 'Feet'].includes(step.bodyPart) ? 1 : 0.3} />
                            <line x1="60" y1="100" x2="65" y2="180" stroke="#7D8E95" strokeWidth="2" opacity={['Upper Legs', 'Lower Legs', 'Feet'].includes(step.bodyPart) ? 1 : 0.3} />
                            <ellipse cx="50" cy="95" rx="20" ry="10" fill="none" stroke="#7D8E95" strokeWidth="2" opacity={step.bodyPart === 'Pelvis & Hips' ? 1 : 0.3} />
                            
                            {/* Whole body glow */}
                            {(step.bodyPart === 'Whole Body' || step.bodyPart === 'Introduction' || step.bodyPart === 'Closing') && (
                                <ellipse cx="50" cy="100" rx="45" ry="95" fill="none" stroke="#7D8E95" strokeWidth="3" opacity="0.6" />
                            )}
                        </svg>
                        
                        {/* Pulsing indicator */}
                        {isPlaying && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-40 h-40 rounded-full bg-[#7D8E95]/20 animate-pulse" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Current step info */}
                <div className="text-center mb-8">
                    <SmallText className="text-[#1D6B6B] font-medium mb-2">
                        {currentStep + 1} of {BODY_SCAN_STEPS.length}
                    </SmallText>
                    <Heading level={2} className="text-[#1D6B6B] mb-4">
                        {step.bodyPart}
                    </Heading>
                    <BodyText className="text-[#2A8585] leading-relaxed mb-4">
                        {step.instruction}
                    </BodyText>
                    <SmallText className="text-[#7D8E95] italic">
                        {step.guidance}
                    </SmallText>
                </div>

                {/* Timer */}
                {isPlaying && (
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                            <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-[#7D8E95]'} ${!isPaused && 'animate-pulse'}`} />
                            <SmallText className="text-[#7D8E95] font-mono">
                                {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* Controls */}
                <div className="flex flex-col items-center gap-4 mb-8">
                    {!isPlaying && (
                        <AudioTip variant="headphones" className="max-w-sm" />
                    )}
                    
                    <div className="flex justify-center gap-4">
                        {!isPlaying ? (
                            <PrimaryButton onClick={handleStart} size="lg" data-testid="start-btn">
                                <Play className="w-5 h-5 mr-2" />
                                Begin Body Scan
                            </PrimaryButton>
                        ) : (
                            <>
                                <IconButton onClick={handleReset} className="bg-white/50" data-testid="reset-btn">
                                    <RotateCcw className="w-5 h-5" />
                                </IconButton>
                                <PrimaryButton onClick={handlePause} data-testid="pause-btn">
                                    {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                                </PrimaryButton>
                                {currentStep < BODY_SCAN_STEPS.length - 1 && (
                                    <IconButton 
                                        onClick={() => handleSkipToStep(currentStep + 1)} 
                                        className="bg-white/50"
                                        data-testid="skip-btn"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </IconButton>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Step indicators */}
                <div className="flex flex-wrap justify-center gap-2">
                    {BODY_SCAN_STEPS.map((s, index) => (
                        <button
                            key={s.id}
                            onClick={() => handleSkipToStep(index)}
                            className={`w-3 h-3 rounded-full transition-all ${
                                index === currentStep 
                                    ? 'bg-[#7D8E95] scale-125' 
                                    : index < currentStep 
                                        ? 'bg-[#7D8E95]/50' 
                                        : 'bg-[#7D8E95]'
                            }`}
                            title={s.bodyPart}
                        />
                    ))}
                </div>
            </main>
        </div>
    );
};

export default BodyScan;
